import validators from '@/helpers/validators';
import useForm from '@/hooks/useForm';
import React, { JSX, useEffect, useState } from 'react';
import { onlyNumbers } from '@/helpers/common';
import { encrypt } from '@/helpers/crypt/crypt';
import {
  getCreditCardLabel,
  updateHolderName,
  updateMaskCard,
  updateMaskCVV,
  validateCVV,
} from '@/helpers/masks/cardNumber';
import { updateMask as cpfCnpjMask } from '@/helpers/masks/cpfCnpj';
import { updateMask as expirationDateMask } from '@/helpers/masks/expirationDate';
import { useDialog } from '@/hooks/useDialog';
import Card from '@/model/Card';
import CardClient from '@/model/CardClient';
import CardClientCrypt from '@/model/CardClientCrypt';
import { setLoading } from '@/redux/loading/loadingSlice';
import { api } from '@/services/api';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCardValidation } from '@/redux/cardValidation/cardValidationSlice';
import { CreditCardParameters } from '@/model/CreditCardParameters';
import { CheckCardWillValidateRequest } from '@/model/CheckCardWillValidateRequest';
import { CheckCardWillValidateResponse } from '@/model/CheckCardWillValidateResponse';
import { AddCreditCardUI } from './ui';
import { FormInputNameCreditCard } from '../types';

export const AddCreditCardScreen: React.FC = (): JSX.Element => {
  const [willValidate, setWillValidate] = useState<boolean>(true);
  const { creditCardParameters } = useSelector(useCardValidation);

  const dispatch = useDispatch();
  const history = useNavigate();

  const { onSetVisible } = useDialog();

  const handleOnCheckIfWillBeValidated = async (parameter: CreditCardParameters): Promise<void> => {
    if (!parameter.openOnProfile && parameter.events && parameter.events.length > 0) {
      try {
        dispatch(setLoading(true));
        const payload: CheckCardWillValidateRequest = {
          events: parameter.events,
        };
        const { data } = await api.post<CheckCardWillValidateResponse>(
          '/client/my-account/card/check-validate',
          payload,
        );
        setWillValidate(data.validate);
        dispatch(setLoading(false));
      } catch {
        dispatch(setLoading(false));
      }
    } else {
      setWillValidate(false);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      number: '',
      month: '',
      year: '',
      cvv: '',
      name: '',
      document: '',
    },
    validators: {
      number: [validators.required, validators.cardNumber],
      month: [validators.required],
      year: [validators.required],
      cvv: [validators.required, validators.cvv],
      name: [validators.required, validators.holderName],
      document: [validators.required, validators.cpforcnpj],
    },
    formatters: {
      number: updateMaskCard,
      date: expirationDateMask,
      cvv: updateMaskCVV,
      name: updateHolderName,
      document: cpfCnpjMask,
    },
  });

  const handleOnAddCreditCard = async (): Promise<void> => {
    if (isFormValid()) {
      if (
        validateCVV(formData[FormInputNameCreditCard.number], formData[FormInputNameCreditCard.cvv])
      ) {
        try {
          dispatch(setLoading(true));
          const card: Card = {
            holder: formData[FormInputNameCreditCard.name],
            number: onlyNumbers(formData[FormInputNameCreditCard.number]),
            expMonth: formData[FormInputNameCreditCard.month],
            expYear: formData[FormInputNameCreditCard.year],
            securityCode: formData[FormInputNameCreditCard.cvv],
            document: onlyNumbers(formData[FormInputNameCreditCard.document]),
            cardType: getCreditCardLabel(formData[FormInputNameCreditCard.number]),
          };
          const encrypted = encrypt(JSON.stringify(card));

          const cardClientCrypt: CardClientCrypt = {
            data: encrypted,
          };
          const response = await api.post<CardClient>('client/my-account/card', cardClientCrypt);

          resetForm();
          onSetVisible(false);
          dispatch(setLoading(false));
          toast.success('Cartão adicionado com sucesso!');
          if (willValidate) {
            history(`/validated-card/${response.data.id}`);
          } else if (!creditCardParameters.openOnProfile) {
            history('/checkout');
          } else {
            history('/profile');
          }
        } catch {
          dispatch(setLoading(false));
        }
      } else {
        toast.warn('Código de segurança inválido!');
      }
    }
  };

  useEffect(() => {
    handleOnCheckIfWillBeValidated(creditCardParameters);
  }, [creditCardParameters]);

  return (
    <AddCreditCardUI
      willValidate={willValidate}
      formData={formData}
      onChangeFormInput={onChangeFormInput}
      formErrors={formErrors}
      onAddCreditCard={handleOnAddCreditCard}
    />
  );
};
